import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  MenuItem,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  ConfirmDialog,
  EmptyState,
  FilterBar,
  LoadingState,
  PageHeader,
  PageLayout,
  ResponsiveCardList,
  SectionCard,
  StatusBadge,
} from '../../components/ui';
import {
  JOB_OFFER_STATUSES,
  deleteJobOffer,
  downloadJobOfferPdf,
  fetchJobOffers,
} from '../../services/jobOffersApi';
import { createRoleCapabilities, parseStoredList } from '../../utils/roleCapabilities';

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

export default function JobOffersListPage() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState('');
  const [perPage, setPerPage] = useState(25);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);

  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const capabilities = useMemo(
    () => createRoleCapabilities({
      role: localStorage.getItem('role') || '',
      actualRole: localStorage.getItem('actual_role') || '',
      permissions: parseStoredList('permissions'),
      isPlatformAdmin: localStorage.getItem('is_platform_admin') === '1',
      userName: localStorage.getItem('name') || '',
      employeeCode: localStorage.getItem('employee_code') || '',
    }),
    [],
  );
  const isHrAdmin = capabilities.hasHrAdminAccess || capabilities.isPlatformAdmin;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchJobOffers({
        search: search || undefined,
        status: status || undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
        per_page: perPage,
        page,
      });
      setItems(Array.isArray(data?.data) ? data.data : []);
      setMeta({
        current_page: data?.current_page || 1,
        last_page: data?.last_page || 1,
        total: data?.total || 0,
      });
    } catch (err) {
      console.error('Failed to load job offers', err);
      setError(err?.response?.data?.message || 'Failed to load job offers.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, sortBy, sortDir, status]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(1);
  };

  const handleResetFilters = () => {
    setSearch('');
    setSearchInput('');
    setStatus('');
    setSortBy('created_at');
    setSortDir('desc');
    setPage(1);
  };

  const handleDeleteConfirmed = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteJobOffer(pendingDelete.id);
      toast.success('Job offer deleted.');
      setPendingDelete(null);
      load();
    } catch (err) {
      console.error('Failed to delete job offer', err);
      toast.error(err?.response?.data?.message || 'Failed to delete job offer.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadPdf = async (offer, variant) => {
    try {
      await downloadJobOfferPdf(offer.id, variant, `${offer.offer_reference}-${variant}.pdf`);
    } catch (err) {
      console.error('Failed to download PDF', err);
      toast.error('Could not download PDF.');
    }
  };

  const sortableHeader = (column, label, align = 'left', sx) => (
    <TableCell align={align} sortDirection={sortBy === column ? sortDir : false} sx={sx}>
      <TableSortLabel
        active={sortBy === column}
        direction={sortBy === column ? sortDir : 'asc'}
        onClick={() => handleSort(column)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const formatPackage = (value) =>
    Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  // Row/card action buttons — shared by the desktop table and the mobile card
  // so the two layouts can never drift.
  const renderActions = (offer) => (
    <>
      <Tooltip title="View">
        <IconButton size="small" onClick={() => navigate(`/hr/job-offers/${offer.id}`)}>
          <VisibilityRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {isHrAdmin && offer.status === 'pending_approval' && (
        <>
          <Tooltip title="Approve & sign">
            <IconButton size="small" color="success" onClick={() => navigate(`/hr/job-offers/${offer.id}`)}>
              <CheckCircleOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Request revision">
            <IconButton size="small" color="info" onClick={() => navigate(`/hr/job-offers/${offer.id}`)}>
              <AutorenewRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reject">
            <IconButton size="small" color="error" onClick={() => navigate(`/hr/job-offers/${offer.id}`)}>
              <HighlightOffRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
      {(offer.status === 'draft' || offer.status === 'revision_requested') && (
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => navigate(`/hr/job-offers/${offer.id}/edit`)}>
            <EditRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Download offer PDF">
        <span>
          <IconButton
            size="small"
            onClick={() => handleDownloadPdf(offer, 'offer')}
            disabled={offer.status === 'draft'}
          >
            <PictureAsPdfRoundedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {(isHrAdmin || ['draft', 'rejected'].includes(offer.status)) && (
        <Tooltip title="Delete">
          <IconButton size="small" color="error" onClick={() => setPendingDelete(offer)}>
            <DeleteOutlineRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  // Mobile / narrow-screen card — same data + actions as a table row, stacked
  // vertically so nothing overflows horizontally on phones or small laptops.
  const renderOfferCard = (offer) => (
    <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
      <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }} noWrap>
              {offer.offer_reference}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {offer.created_at ? new Date(offer.created_at).toLocaleDateString() : '—'}
            </Typography>
          </Box>
          <StatusBadge status={offer.status} />
        </Stack>

        <Box sx={{ mt: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 700 }} noWrap>
            {offer.candidate_name}
          </Typography>
          {offer.candidate_name_ar ? (
            <Typography variant="caption" color="text.secondary" sx={{ direction: 'rtl', display: 'block' }}>
              {offer.candidate_name_ar}
            </Typography>
          ) : null}
          <Typography variant="body2" color="text.secondary" noWrap>
            {offer.position || '—'}
            {offer.project_name ? ` · ${offer.project_name}` : ''}
          </Typography>
        </Box>

        <Divider sx={{ my: 1.25 }} />

        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block' }}>
              Total Package
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
              {formatPackage(offer.proposed_total_package)}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.25} sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {renderActions(offer)}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );

  // Columns beyond the essentials are hidden on small laptop widths (md–lg) so
  // the table fits without horizontal scrolling; they return at lg and up.
  const secondaryColSx = { display: { xs: 'none', lg: 'table-cell' } };

  const totalLabel = useMemo(() => `${meta.total} offer${meta.total === 1 ? '' : 's'}`, [meta.total]);

  return (
    <PageLayout backgroundAccent>
      <PageHeader
        overline="Recruitment"
        title="Job Offers"
        subtitle="Create, review, approve and download bilingual offer letters."
        actions={
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => navigate('/hr/job-offers/new')}
          >
            New Job Offer
          </Button>
        }
      />

      <FilterBar
        onReset={handleResetFilters}
        left={(
          <>
            <TextField
              size="small"
              placeholder="Search candidate, reference, position..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ width: { xs: '100%', sm: 280 } }}
            />
            <TextField
              size="small"
              select
              label="Status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              sx={{ width: { xs: '100%', sm: 180 } }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {JOB_OFFER_STATUSES.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              label="Per page"
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
              sx={{ width: { xs: '100%', sm: 120 } }}
            >
              {PER_PAGE_OPTIONS.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
          </>
        )}
        right={(
          <Typography variant="body2" color="text.secondary">
            {totalLabel}
          </Typography>
        )}
      />

      <SectionCard sx={{ mt: 2 }}>
        {loading ? (
          <LoadingState rows={6} header={false} />
        ) : error ? (
          <EmptyState
            compact
            title="Could not load offers"
            description={error}
            actionLabel="Try again"
            onAction={load}
          />
        ) : items.length === 0 ? (
          <EmptyState
            compact
            title="No job offers yet"
            description="Create the first offer to start the HR-admin approval workflow."
            actionLabel="New Job Offer"
            onAction={() => navigate('/hr/job-offers/new')}
          />
        ) : (
          <>
            <ResponsiveCardList
              items={items}
              breakpoint="md"
              renderCard={renderOfferCard}
              desktop={(
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 640 }}>
                    <TableHead>
                      <TableRow>
                        {sortableHeader('offer_reference', 'Reference')}
                        {sortableHeader('candidate_name', 'Candidate')}
                        {sortableHeader('position', 'Position')}
                        {sortableHeader('project_name', 'Project', 'left', secondaryColSx)}
                        {sortableHeader('proposed_total_package', 'Total Package', 'right')}
                        {sortableHeader('status', 'Status')}
                        {sortableHeader('created_at', 'Created', 'left', secondaryColSx)}
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((offer) => (
                        <TableRow key={offer.id} hover>
                          <TableCell sx={{ fontWeight: 700 }}>{offer.offer_reference}</TableCell>
                          <TableCell>
                            <Stack spacing={0.25}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {offer.candidate_name}
                              </Typography>
                              {offer.candidate_name_ar ? (
                                <Typography variant="caption" color="text.secondary" sx={{ direction: 'rtl' }}>
                                  {offer.candidate_name_ar}
                                </Typography>
                              ) : null}
                            </Stack>
                          </TableCell>
                          <TableCell>{offer.position}</TableCell>
                          <TableCell sx={secondaryColSx}>{offer.project_name || '—'}</TableCell>
                          <TableCell align="right">{formatPackage(offer.proposed_total_package)}</TableCell>
                          <TableCell>
                            <StatusBadge status={offer.status} />
                          </TableCell>
                          <TableCell sx={secondaryColSx}>
                            {offer.created_at ? new Date(offer.created_at).toLocaleDateString() : '—'}
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              {renderActions(offer)}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Page {meta.current_page} of {meta.last_page}
              </Typography>
              <Pagination
                size="small"
                color="primary"
                page={meta.current_page}
                count={meta.last_page}
                onChange={(_, value) => setPage(value)}
              />
            </Box>
          </>
        )}
      </SectionCard>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete job offer?"
        description={`This will remove offer ${pendingDelete?.offer_reference || ''}. This cannot be undone.`}
        confirmLabel={deleting ? 'Deleting...' : 'Delete'}
        confirmColor="error"
        loading={deleting}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleDeleteConfirmed}
      />
    </PageLayout>
  );
}
